import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getProfile } from '../services/profileService';
import {
  addLabData,
  addCreatinine, addBUN, addEGFR,
  addWBC, addRBC, addHemoglobin, addHematocrit, addPlatelet,
  addHbA1c,
  addCholesterol, addHDL, addLDL, addTriglyceride,
  addSGOT, addSGPT, addAlkalinePhosphatase,
  addUricAcid
} from '../services/labService';

const HospitalFormContainer = styled.div`
  min-height: 100vh;
  background-color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const Header = styled.div`
  background: white;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: bold;
  color: #666;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const ProfileImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const NotificationContainer = styled.div`
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
  margin-right: 8px;

  &:hover {
    transform: scale(1.1);
  }
`;

const BellIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z' fill='%23666666'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: #ff4444;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Noto Sans Thai', sans-serif !important;
  min-width: 16px;
  padding: 0 2px;
`;

const ProfileName = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 0;
  max-width: 100%;
  margin: 0;
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: normal;
  color: white;
  background-color: #00BBD3;
  text-align: left;
  margin: 0 0 32px 0;
  padding: 16px 24px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormSection = styled.div`
  padding: 0 24px;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  color: #666666;
  margin-bottom: 24px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  line-height: 1.5;
`;

const CheckMark = styled.span`
  color: #00BBD3;
  font-size: 18px;
  font-weight: bold;
`;

const RedText = styled.span`
  color: #ff0000;
  font-weight: bold;
`;

const InputGroup = styled.div`
  margin-bottom: 24px;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const InputField = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }

  &::placeholder {
    color: #cccccc;
  }

  /* Hide "BE" prefix in date input for English locale */
  &[type="date"]::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    color: transparent;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
  }

  &[type="date"]::-webkit-datetime-edit-year-field:before {
    content: attr(data-year) !important;
  }

  &[type="date"] {
    position: relative;
  }
`;

const TimeInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const CheckboxSection = styled.div`
  margin-bottom: 32px;
`;

const CheckboxGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const CheckboxItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    transform: translateX(4px);
    background-color: rgba(0, 187, 211, 0.02);
    border-radius: 8px;
    padding: 16px 8px;
  }
`;

const CheckboxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  border: 2px solid #00BBD3;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: scale(1);
  box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 0 4px rgba(0, 187, 211, 0.1);
    border-color: #009bb3;
  }
  
  &:checked {
    background-color: #00BBD3;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e");
    background-size: 12px;
    background-position: center;
    background-repeat: no-repeat;
    transform: scale(1.15);
    animation: checkPulse 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @keyframes checkPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0.4);
    }
    50% {
      transform: scale(1.2);
      box-shadow: 0 0 0 8px rgba(0, 187, 211, 0.2);
    }
    100% {
      transform: scale(1.15);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
    }
  }
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  color: #666666;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  flex: 1;
  font-weight: bold;
`;

const SubFormContainer = styled.div`
  margin-left: 32px;
  background-color: white;
  border-radius: 12px;
  padding: ${props => props.show ? '20px' : '0 20px'};
  overflow: hidden;
  max-height: ${props => props.show ? '2000px' : '0'};
  opacity: ${props => props.show ? '1' : '0'};
  transform: translateY(${props => props.show ? '0' : '-10px'});
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: max-height, opacity, transform, padding;
  backface-visibility: hidden;
  transform-style: preserve-3d;
`;

const SubInputGroup = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SubInputLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: normal;
  color: #333333;
  font-family: 'Noto Sans Thai', sans-serif !important;
  margin-bottom: 8px;
`;

const SubInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SubInputField = styled.input`
  width: 120px;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 20px;
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
  
  &::placeholder {
    color: #cccccc;
  }
`;

const UnitLabel = styled.span`
  font-size: 14px;
  color: #999999;
  font-family: 'Noto Sans Thai', sans-serif !important;
  white-space: nowrap;
  min-width: fit-content;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioButton = styled.input.attrs({ type: 'radio' })`
  width: 16px;
  height: 16px;
  border: 2px solid #00BBD3;
  border-radius: 50%;
  background-color: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: scale(1);
  box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
  
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 0 0 4px rgba(0, 187, 211, 0.1);
    border-color: #009bb3;
  }
  
  &:checked {
    background-color: #00BBD3;
    background-image: radial-gradient(circle, white 30%, transparent 30%);
    transform: scale(1.2);
    animation: radioPulse 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:active {
    transform: scale(0.9);
  }
  
  @keyframes radioPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0.4);
    }
    50% {
      transform: scale(1.3);
      box-shadow: 0 0 0 6px rgba(0, 187, 211, 0.2);
    }
    100% {
      transform: scale(1.2);
      box-shadow: 0 0 0 0 rgba(0, 187, 211, 0);
    }
  }
`;

const RadioLabel = styled.label`
  font-size: 16px;
  color: #666666;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const SaveButton = styled.button`
  background-color: #00BBD3;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 18px 32px;
  font-size: 18px;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);
  margin: 20px 0;

  &:hover {
    background-color: #009bb3;
  }

  &:active {
    background-color: #008aa0;
  }
`;

const HospitalCheckupForm = () => {
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(3);
  const [isSaving, setIsSaving] = useState(false);
  const [userData, setUserData] = useState({
    name: '',
    profileImage: null
  });
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    hospital: '',
    checkedItems: {
      kidney: false,
      bloodSugar: false,
      cholesterol: false,
      liver: false,
      blood: false,
      uricAcid: false,
      gout: false
    },
    testValues: {
      kidney: {
        creatinine: '',
        bun: '',
        egfr: ''
      },
      bloodSugar: {
        hba1c: ''
      },
      cholesterol: {
        hdl: '',
        ldl: '',
        cholesterol: '',
        triglyceride: ''
      },
      liver: {
        sgpt: '',
        sgot: '',
        alp: '',
        bilirubin: '',
        albumin: '',
        protein: '',
        ggt: ''
      },
      blood: {
        rbc: '',
        hemoglobin: '',
        hematocrit: '',
        wbc: '',
        platelets: '',
        mcv: '',
        mch: '',
        mchc: ''
      },
      uricAcid: {
        color: '',
        clarity: '',
        ph: '',
        specificGravity: '',
        protein: '',
        glucose: '',
        ketones: '',
        blood: '',
        leukocytes: '',
        nitrite: '',
        bilirubin: '',
        urobilinogen: '',
        redBloodCells: '',
        whiteBloodCells: ''
      },
      gout: {
        uricAcid: ''
      }
    }
  });

  const handleNotificationClick = () => {
    navigate('/notifications');
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await getProfile();
        let profile = response?.data || response?.profile;

        // Get image path and convert to full Firebase URL
        let imagePath = profile.image || profile.profile_image || '';
        let imageUrl = null;
        if (imagePath && imagePath !== '') {
          if (imagePath.startsWith('http')) {
            imageUrl = imagePath;
          } else {
            imageUrl = `https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/${imagePath}`;
          }
        }

        setUserData({
          name: profile.name || profile.firstname || '',
          profileImage: imageUrl
        });
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };

    loadProfile();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (item) => {
    setFormData(prev => ({
      ...prev,
      checkedItems: {
        ...prev.checkedItems,
        [item]: !prev.checkedItems[item]
      }
    }));
  };

  const handleTestValueChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      testValues: {
        ...prev.testValues,
        [category]: {
          ...prev.testValues[category],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    if (isSaving) return; // ป้องกันการคลิกซ้ำ

    try {
      setIsSaving(true);
      console.log('บันทึกข้อมูลตรวจจากโรงพยาบาล:', formData);

      // Create datetime from form data
      const datetime = `${formData.date} ${formData.time}:00`;

      // Save Kidney data if checked
      if (formData.checkedItems.kidney) {
        const { creatinine, bun, egfr } = formData.testValues.kidney;

        if (creatinine) {
          await addCreatinine(creatinine, datetime);
        }

        if (bun) {
          await addBUN(bun, datetime);
        }

        if (egfr) {
          await addEGFR(egfr, datetime);
        }
      }

      // Save CBC (ความสมบูรณ์ของเลือด) data if checked
      if (formData.checkedItems.blood) {
        const { wbc, rbc, hemoglobin, hematocrit, platelets, mcv, mch, mchc } = formData.testValues.blood;

        if (wbc) {
          await addWBC(wbc, datetime);
        }

        if (rbc) {
          await addRBC(rbc, datetime);
        }

        if (hemoglobin) {
          await addHemoglobin(hemoglobin, datetime);
        }

        if (hematocrit) {
          await addHematocrit(hematocrit, datetime);
        }

        if (platelets) {
          await addPlatelet(platelets, datetime);
        }

        if (mcv) {
          await addLabData([{ dateadd: datetime, name: 'mcv', value: mcv.toString() }]);
        }

        if (mch) {
          await addLabData([{ dateadd: datetime, name: 'mch', value: mch.toString() }]);
        }

        if (mchc) {
          await addLabData([{ dateadd: datetime, name: 'mchc', value: mchc.toString() }]);
        }
      }

      // Save HbA1c (น้ำตาลสะสม) data if checked
      if (formData.checkedItems.bloodSugar) {
        const { hba1c } = formData.testValues.bloodSugar;

        if (hba1c) {
          await addHbA1c(hba1c, datetime);
        }
      }

      // Save Lipid (ไขมัน) data if checked
      if (formData.checkedItems.cholesterol) {
        const { cholesterol, hdl, ldl, triglyceride } = formData.testValues.cholesterol;

        if (cholesterol) {
          await addCholesterol(cholesterol, datetime);
        }

        if (hdl) {
          await addHDL(hdl, datetime);
        }

        if (ldl) {
          await addLDL(ldl, datetime);
        }

        if (triglyceride) {
          await addTriglyceride(triglyceride, datetime);
        }
      }

      // Save Liver (ตับ) data if checked
      if (formData.checkedItems.liver) {
        const { sgot, sgpt, alp, bilirubin, albumin, protein, ggt } = formData.testValues.liver;

        if (sgot) {
          await addSGOT(sgot, datetime);
        }

        if (sgpt) {
          await addSGPT(sgpt, datetime);
        }

        if (alp) {
          await addAlkalinePhosphatase(alp, datetime);
        }

        if (bilirubin) {
          await addLabData([{ dateadd: datetime, name: 'bilirubin', value: bilirubin.toString() }]);
        }

        if (albumin) {
          await addLabData([{ dateadd: datetime, name: 'albumin', value: albumin.toString() }]);
        }

        if (protein) {
          await addLabData([{ dateadd: datetime, name: 'protein', value: protein.toString() }]);
        }

        if (ggt) {
          await addLabData([{ dateadd: datetime, name: 'ggt', value: ggt.toString() }]);
        }
      }

      // Save Uric Acid (กรดยูริค) data if checked
      if (formData.checkedItems.gout) {
        const { uricAcid } = formData.testValues.gout;

        if (uricAcid) {
          await addUricAcid(uricAcid, datetime);
        }
      }

      // Save Urine Test data if checked
      if (formData.checkedItems.uricAcid) {
        const urineData = formData.testValues.uricAcid;

        // Save each urine parameter
        if (urineData.ph) {
          await addLabData([{ dateadd: datetime, name: 'ph', value: urineData.ph.toString() }]);
        }
        if (urineData.color) {
          await addLabData([{ dateadd: datetime, name: 'color', value: urineData.color.toString() }]);
        }
        if (urineData.clarity) {
          await addLabData([{ dateadd: datetime, name: 'clarity', value: urineData.clarity.toString() }]);
        }
        if (urineData.specificGravity) {
          await addLabData([{ dateadd: datetime, name: 'specificGravity', value: urineData.specificGravity.toString() }]);
        }
        if (urineData.protein) {
          await addLabData([{ dateadd: datetime, name: 'protein', value: urineData.protein.toString() }]);
        }
        if (urineData.glucose) {
          await addLabData([{ dateadd: datetime, name: 'glucose', value: urineData.glucose.toString() }]);
        }
        if (urineData.ketones) {
          await addLabData([{ dateadd: datetime, name: 'ketones', value: urineData.ketones.toString() }]);
        }
        if (urineData.blood) {
          await addLabData([{ dateadd: datetime, name: 'blood', value: urineData.blood.toString() }]);
        }
        if (urineData.bilirubin) {
          await addLabData([{ dateadd: datetime, name: 'bilirubin', value: urineData.bilirubin.toString() }]);
        }
        if (urineData.urobilinogen) {
          await addLabData([{ dateadd: datetime, name: 'urobilinogen', value: urineData.urobilinogen.toString() }]);
        }
        if (urineData.leukocytes) {
          await addLabData([{ dateadd: datetime, name: 'leukocytes', value: urineData.leukocytes.toString() }]);
        }
        if (urineData.nitrite) {
          await addLabData([{ dateadd: datetime, name: 'nitrite', value: urineData.nitrite.toString() }]);
        }
        if (urineData.redBloodCells) {
          await addLabData([{ dateadd: datetime, name: 'redBloodCells', value: urineData.redBloodCells.toString() }]);
        }
        if (urineData.whiteBloodCells) {
          await addLabData([{ dateadd: datetime, name: 'whiteBloodCells', value: urineData.whiteBloodCells.toString() }]);
        }
      }

      // Save Stool Test data if checked (assuming there's a stool checkbox)
      if (formData.checkedItems.stool && formData.testValues.stool) {
        const stoolData = formData.testValues.stool;

        if (stoolData.stoolColor) {
          await addLabData([{ dateadd: datetime, name: 'stoolColor', value: stoolData.stoolColor.toString() }]);
        }
        if (stoolData.stoolConsistency) {
          await addLabData([{ dateadd: datetime, name: 'stoolConsistency', value: stoolData.stoolConsistency.toString() }]);
        }
        if (stoolData.stoolRBC) {
          await addLabData([{ dateadd: datetime, name: 'stoolRBC', value: stoolData.stoolRBC.toString() }]);
        }
        if (stoolData.stoolWBC) {
          await addLabData([{ dateadd: datetime, name: 'stoolWBC', value: stoolData.stoolWBC.toString() }]);
        }
        if (stoolData.stoolParasite) {
          await addLabData([{ dateadd: datetime, name: 'stoolParasite', value: stoolData.stoolParasite.toString() }]);
        }
        if (stoolData.stoolOccultBlood) {
          await addLabData([{ dateadd: datetime, name: 'stoolOccultBlood', value: stoolData.stoolOccultBlood.toString() }]);
        }
      }

      // Note: X-ray is not connected to API yet

      alert('บันทึกข้อมูลเรียบร้อยแล้ว');
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Error saving hospital data:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <HospitalFormContainer>
      <Header>
        <HeaderLeft>
          <Logo src="./logo.png" alt="MFU Logo" />
          <HeaderTitle>MFU Wellness Center</HeaderTitle>
        </HeaderLeft>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <NotificationContainer onClick={handleNotificationClick}>
            <BellIcon />
            {notificationCount > 0 && (
              <NotificationBadge>{notificationCount}</NotificationBadge>
            )}
          </NotificationContainer>
          <ProfileContainer onClick={() => navigate('/profile-edit')}>
            <ProfileName>{userData.name || 'ผู้ใช้'}</ProfileName>
            <ProfileImage src={userData.profileImage || './person.png'} alt="Profile" />
          </ProfileContainer>
        </div>
      </Header>

      <Content>
        <PageTitle>เพิ่มผลตรวจจากโรงพยาบาล</PageTitle>
        
        <FormSection>
          <SectionTitle>ตรวจจากโรงพยาบาล</SectionTitle>
          <SectionSubtitle>
            ท่านสามารถกด <CheckMark>✓</CheckMark> ที่ช่องเพื่อกรอกข้อมูลเฉพาะที่ต้องการบันทึกผลตรวจ{' '}
            <RedText>(ไม่ต้องกรอกทั้งหมด)</RedText>
          </SectionSubtitle>

          <InputGroup>
            <InputLabel>วันที่ตรวจ</InputLabel>
            <InputField
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              style={{
                colorScheme: 'light',
                position: 'relative'
              }}
              pattern="\d{1,2}/\d{1,2}/\d{4}"
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>เวลาที่บันทึก</InputLabel>
            <TimeInput
              type="time"
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>ตรวจที่</InputLabel>
            <InputField
              type="text"
              placeholder="ตรวจที่"
              value={formData.hospital}
              onChange={(e) => handleInputChange('hospital', e.target.value)}
            />
          </InputGroup>

          <CheckboxSection>
            <CheckboxGrid>
              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="kidney"
                    checked={formData.checkedItems.kidney}
                    onChange={() => handleCheckboxChange('kidney')}
                  />
                  <CheckboxLabel htmlFor="kidney">การทำงานของไต</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.kidney}>
                  <SubInputGroup>
                    <SubInputLabel>Creatinine</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="Creatinine"
                        value={formData.testValues.kidney.creatinine}
                        onChange={(e) => handleTestValueChange('kidney', 'creatinine', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>BUN , Urea (Blood Urea Nitrogen)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="BUN , Urea"
                        value={formData.testValues.kidney.bun}
                        onChange={(e) => handleTestValueChange('kidney', 'bun', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>eGFR</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="eGFR"
                        value={formData.testValues.kidney.egfr}
                        onChange={(e) => handleTestValueChange('kidney', 'egfr', e.target.value)}
                      />
                      <UnitLabel>mL/min/1.73m²</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="bloodSugar"
                    checked={formData.checkedItems.bloodSugar}
                    onChange={() => handleCheckboxChange('bloodSugar')}
                  />
                  <CheckboxLabel htmlFor="bloodSugar">น้ำตาลเฉลี่ยสะสม</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.bloodSugar}>
                  <SubInputGroup>
                    <SubInputLabel>HbA1C</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.1"
                        placeholder="HbA1C"
                        value={formData.testValues.bloodSugar.hba1c}
                        onChange={(e) => handleTestValueChange('bloodSugar', 'hba1c', e.target.value)}
                      />
                      <UnitLabel>%</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="cholesterol"
                    checked={formData.checkedItems.cholesterol}
                    onChange={() => handleCheckboxChange('cholesterol')}
                  />
                  <CheckboxLabel htmlFor="cholesterol">ไขมันในเลือด</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.cholesterol}>
                  <SubInputGroup>
                    <SubInputLabel>HDL</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="HDL"
                        value={formData.testValues.cholesterol.hdl}
                        onChange={(e) => handleTestValueChange('cholesterol', 'hdl', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>LDL</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="LDL"
                        value={formData.testValues.cholesterol.ldl}
                        onChange={(e) => handleTestValueChange('cholesterol', 'ldl', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>คอเลสเตอรอลรวม</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="คอเลสเตอรอลรวม"
                        value={formData.testValues.cholesterol.cholesterol}
                        onChange={(e) => handleTestValueChange('cholesterol', 'cholesterol', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>ไตรกลีเซอไรด์</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="ไตรกลีเซอไรด์"
                        value={formData.testValues.cholesterol.triglyceride}
                        onChange={(e) => handleTestValueChange('cholesterol', 'triglyceride', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="liver"
                    checked={formData.checkedItems.liver}
                    onChange={() => handleCheckboxChange('liver')}
                  />
                  <CheckboxLabel htmlFor="liver">การทำงานของตับ</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.liver}>
                  <SubInputGroup>
                    <SubInputLabel>SGPT (ALT)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="SGPT"
                        value={formData.testValues.liver.sgpt}
                        onChange={(e) => handleTestValueChange('liver', 'sgpt', e.target.value)}
                      />
                      <UnitLabel>U/L</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>SGOT (AST)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="SGOT"
                        value={formData.testValues.liver.sgot}
                        onChange={(e) => handleTestValueChange('liver', 'sgot', e.target.value)}
                      />
                      <UnitLabel>U/L</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>ALP (Alkaline Phosphatase)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="ALP"
                        value={formData.testValues.liver.alp}
                        onChange={(e) => handleTestValueChange('liver', 'alp', e.target.value)}
                      />
                      <UnitLabel>U/L</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>Total Bilirubin</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="Total Bilirubin"
                        value={formData.testValues.liver.bilirubin}
                        onChange={(e) => handleTestValueChange('liver', 'bilirubin', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>อัลบูมิน (Albumin)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="อัลบูมิน"
                        value={formData.testValues.liver.albumin}
                        onChange={(e) => handleTestValueChange('liver', 'albumin', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>โปรตีนรวม (Total Protein)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="โปรตีนรวม"
                        value={formData.testValues.liver.protein}
                        onChange={(e) => handleTestValueChange('liver', 'protein', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>GGT (Gamma-glutamyl transferase)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="GGT"
                        value={formData.testValues.liver.ggt}
                        onChange={(e) => handleTestValueChange('liver', 'ggt', e.target.value)}
                      />
                      <UnitLabel>U/L</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="blood"
                    checked={formData.checkedItems.blood}
                    onChange={() => handleCheckboxChange('blood')}
                  />
                  <CheckboxLabel htmlFor="blood">ความสมบูรณ์ของเลือด</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.blood}>
                  <SubInputGroup>
                    <SubInputLabel>จำนวนเม็ดเลือดแดง (RBC)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="เม็ดเลือดแดง"
                        value={formData.testValues.blood.rbc}
                        onChange={(e) => handleTestValueChange('blood', 'rbc', e.target.value)}
                      />
                      <UnitLabel>million cells/μL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>ฮีโมโกลบิน (Hemoglobin, Hb)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="ฮีโมโกลบิน"
                        value={formData.testValues.blood.hemoglobin}
                        onChange={(e) => handleTestValueChange('blood', 'hemoglobin', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>ฮีมาโตคริต (Hematocrit, Hct)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="ฮีมาโตคริต"
                        value={formData.testValues.blood.hematocrit}
                        onChange={(e) => handleTestValueChange('blood', 'hematocrit', e.target.value)}
                      />
                      <UnitLabel>%</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>จำนวนเม็ดเลือดขาว (WBC)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="เม็ดเลือดขาว"
                        value={formData.testValues.blood.wbc}
                        onChange={(e) => handleTestValueChange('blood', 'wbc', e.target.value)}
                      />
                      <UnitLabel>cells/μL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>เกล็ดเลือด (Platelets)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="เกล็ดเลือด"
                        value={formData.testValues.blood.platelets}
                        onChange={(e) => handleTestValueChange('blood', 'platelets', e.target.value)}
                      />
                      <UnitLabel>cells/μL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>MCV</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="MCV"
                        value={formData.testValues.blood.mcv}
                        onChange={(e) => handleTestValueChange('blood', 'mcv', e.target.value)}
                      />
                      <UnitLabel>fL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>MCH</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="MCH"
                        value={formData.testValues.blood.mch}
                        onChange={(e) => handleTestValueChange('blood', 'mch', e.target.value)}
                      />
                      <UnitLabel>pg/cell</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                  <SubInputGroup>
                    <SubInputLabel>MCHC</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="MCHC"
                        value={formData.testValues.blood.mchc}
                        onChange={(e) => handleTestValueChange('blood', 'mchc', e.target.value)}
                      />
                      <UnitLabel>g/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="uricAcid"
                    checked={formData.checkedItems.uricAcid}
                    onChange={() => handleCheckboxChange('uricAcid')}
                  />
                  <CheckboxLabel htmlFor="uricAcid">ผลการตรวจปัสสาวะ</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.uricAcid}>
                  <SubInputGroup>
                    <SubInputLabel>สีของปัสสาวะ (Color)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="color-normal"
                          name="color"
                          value="normal"
                          checked={formData.testValues.uricAcid.color === 'normal'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'color', e.target.value)}
                        />
                        <RadioLabel htmlFor="color-normal">เหลืองอ่อน (สีปัสสาวะปกติ)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="color-abnormal"
                          name="color"
                          value="abnormal"
                          checked={formData.testValues.uricAcid.color === 'abnormal'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'color', e.target.value)}
                        />
                        <RadioLabel htmlFor="color-abnormal">ผิดปกติ</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>
                  
                  <SubInputGroup>
                    <SubInputLabel>ความใส (Clarity)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="clarity-normal"
                          name="clarity"
                          value="normal"
                          checked={formData.testValues.uricAcid.clarity === 'normal'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'clarity', e.target.value)}
                        />
                        <RadioLabel htmlFor="clarity-normal">ชัดเจน (ไม่มีตะกอน)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="clarity-abnormal"
                          name="clarity"
                          value="abnormal"
                          checked={formData.testValues.uricAcid.clarity === 'abnormal'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'clarity', e.target.value)}
                        />
                        <RadioLabel htmlFor="clarity-abnormal">ผิดปกติ</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>pH (ค่า pH)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.1"
                        placeholder="pH"
                        value={formData.testValues.uricAcid.ph}
                        onChange={(e) => handleTestValueChange('uricAcid', 'ph', e.target.value)}
                      />
                    </SubInputWrapper>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>ความถ่วงจำเพาะ (Specific Gravity)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.001"
                        placeholder="ความถ่วงจำเพาะ"
                        value={formData.testValues.uricAcid.specificGravity}
                        onChange={(e) => handleTestValueChange('uricAcid', 'specificGravity', e.target.value)}
                      />
                    </SubInputWrapper>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>โปรตีน (Protein)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="protein-negative"
                          name="protein"
                          value="negative"
                          checked={formData.testValues.uricAcid.protein === 'negative'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'protein', e.target.value)}
                        />
                        <RadioLabel htmlFor="protein-negative">ไม่มีโปรตีน (Negative)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="protein-positive"
                          name="protein"
                          value="positive"
                          checked={formData.testValues.uricAcid.protein === 'positive'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'protein', e.target.value)}
                        />
                        <RadioLabel htmlFor="protein-positive">มีโปรตีน (Positive)</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>น้ำตาล (Glucose)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="glucose-negative"
                          name="glucose"
                          value="negative"
                          checked={formData.testValues.uricAcid.glucose === 'negative'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'glucose', e.target.value)}
                        />
                        <RadioLabel htmlFor="glucose-negative">ไม่มีน้ำตาล (Negative)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="glucose-positive"
                          name="glucose"
                          value="positive"
                          checked={formData.testValues.uricAcid.glucose === 'positive'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'glucose', e.target.value)}
                        />
                        <RadioLabel htmlFor="glucose-positive">มีน้ำตาล (Positive)</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>คีโตน (Ketones)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="ketones-negative"
                          name="ketones"
                          value="negative"
                          checked={formData.testValues.uricAcid.ketones === 'negative'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'ketones', e.target.value)}
                        />
                        <RadioLabel htmlFor="ketones-negative">ไม่มีคีโตน (Negative)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="ketones-positive"
                          name="ketones"
                          value="positive"
                          checked={formData.testValues.uricAcid.ketones === 'positive'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'ketones', e.target.value)}
                        />
                        <RadioLabel htmlFor="ketones-positive">มีคีโตน (Positive)</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>เลือด (Blood)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="blood-negative"
                          name="urineBlood"
                          value="negative"
                          checked={formData.testValues.uricAcid.blood === 'negative'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'blood', e.target.value)}
                        />
                        <RadioLabel htmlFor="blood-negative">ไม่มีเลือด (Negative)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="blood-positive"
                          name="urineBlood"
                          value="positive"
                          checked={formData.testValues.uricAcid.blood === 'positive'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'blood', e.target.value)}
                        />
                        <RadioLabel htmlFor="blood-positive">มีเลือด (Positive)</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>เม็ดเลือดขาว (Leukocytes)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="leukocytes-negative"
                          name="leukocytes"
                          value="negative"
                          checked={formData.testValues.uricAcid.leukocytes === 'negative'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'leukocytes', e.target.value)}
                        />
                        <RadioLabel htmlFor="leukocytes-negative">ไม่มีเม็ดเลือดขาว (Negative)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="leukocytes-positive"
                          name="leukocytes"
                          value="positive"
                          checked={formData.testValues.uricAcid.leukocytes === 'positive'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'leukocytes', e.target.value)}
                        />
                        <RadioLabel htmlFor="leukocytes-positive">มีเม็ดเลือดขาว (Positive)</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>ไนเตรต (Nitrite)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="nitrite-negative"
                          name="nitrite"
                          value="negative"
                          checked={formData.testValues.uricAcid.nitrite === 'negative'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'nitrite', e.target.value)}
                        />
                        <RadioLabel htmlFor="nitrite-negative">ไม่มีไนเตรต (Negative)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="nitrite-positive"
                          name="nitrite"
                          value="positive"
                          checked={formData.testValues.uricAcid.nitrite === 'positive'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'nitrite', e.target.value)}
                        />
                        <RadioLabel htmlFor="nitrite-positive">มีไนเตรต (Positive)</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>บิลิรูบิน (Bilirubin)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="bilirubin-negative"
                          name="urineBilirubin"
                          value="negative"
                          checked={formData.testValues.uricAcid.bilirubin === 'negative'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'bilirubin', e.target.value)}
                        />
                        <RadioLabel htmlFor="bilirubin-negative">ไม่มีบิลิรูบิน (Negative)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="bilirubin-positive"
                          name="urineBilirubin"
                          value="positive"
                          checked={formData.testValues.uricAcid.bilirubin === 'positive'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'bilirubin', e.target.value)}
                        />
                        <RadioLabel htmlFor="bilirubin-positive">มีบิลิรูบิน (Positive)</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>ยูโรบิลิโนเจน (Urobilinogen)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        step="0.1"
                        placeholder="Urobilinogen"
                        value={formData.testValues.uricAcid.urobilinogen}
                        onChange={(e) => handleTestValueChange('uricAcid', 'urobilinogen', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>เซลล์เม็ดเลือดแดง (Red Blood Cells)</SubInputLabel>
                    <RadioGroup>
                      <RadioOption>
                        <RadioButton
                          id="rbc-negative"
                          name="redBloodCells"
                          value="negative"
                          checked={formData.testValues.uricAcid.redBloodCells === 'negative'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'redBloodCells', e.target.value)}
                        />
                        <RadioLabel htmlFor="rbc-negative">ไม่มีเซลล์เม็ดเลือดแดง (Negative)</RadioLabel>
                      </RadioOption>
                      <RadioOption>
                        <RadioButton
                          id="rbc-positive"
                          name="redBloodCells"
                          value="positive"
                          checked={formData.testValues.uricAcid.redBloodCells === 'positive'}
                          onChange={(e) => handleTestValueChange('uricAcid', 'redBloodCells', e.target.value)}
                        />
                        <RadioLabel htmlFor="rbc-positive">มีเซลล์เม็ดเลือดแดง (Positive)</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                  </SubInputGroup>

                  <SubInputGroup>
                    <SubInputLabel>เซลล์เม็ดเลือดขาว (White Blood Cells)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="White Blood Cells"
                        value={formData.testValues.uricAcid.whiteBloodCells}
                        onChange={(e) => handleTestValueChange('uricAcid', 'whiteBloodCells', e.target.value)}
                      />
                      <UnitLabel>cells/HPF</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>

              <CheckboxItem>
                <CheckboxHeader>
                  <Checkbox
                    id="gout"
                    checked={formData.checkedItems.gout}
                    onChange={() => handleCheckboxChange('gout')}
                  />
                  <CheckboxLabel htmlFor="gout">กรดยูริค</CheckboxLabel>
                </CheckboxHeader>
                <SubFormContainer show={formData.checkedItems.gout}>
                  <SubInputGroup>
                    <SubInputLabel>กรดยูริค (Uric Acid)</SubInputLabel>
                    <SubInputWrapper>
                      <SubInputField
                        type="number"
                        placeholder="กรดยูริค"
                        value={formData.testValues.gout.uricAcid}
                        onChange={(e) => handleTestValueChange('gout', 'uricAcid', e.target.value)}
                      />
                      <UnitLabel>mg/dL</UnitLabel>
                    </SubInputWrapper>
                  </SubInputGroup>
                </SubFormContainer>
              </CheckboxItem>
            </CheckboxGrid>
          </CheckboxSection>

          <SaveButton onClick={handleSave} disabled={isSaving} style={{ opacity: isSaving ? 0.6 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}>
            {isSaving ? 'กำลังบันทึก...' : 'บันทึก / กลับสู่หน้าหลัก'}
          </SaveButton>
        </FormSection>
      </Content>
    </HospitalFormContainer>
  );
};

export default HospitalCheckupForm;